import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { releaseAPI, modulesAPI, usersAPI, restaurantsAPI, tasksAPI, downloadBlob, deleteAttachment } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { AttachmentGallery } from '../components/AttachmentGallery';
import { MultiSelect } from '../components/MultiSelect';
import DevTaskModal from '../components/DevTaskModal';
import {
  ChevronDown, ChevronRight, FileText, FileSpreadsheet, FileType, Presentation,
  Trash2, Image as ImageIcon, Calendar, Loader2, Store, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

const EXPORTS = [
  { format: 'md', label: 'MD', icon: FileText, ext: 'md' },
  { format: 'pdf', label: 'PDF', icon: FileType, ext: 'pdf' },
  { format: 'xlsx', label: 'XLSX', icon: FileSpreadsheet, ext: 'xlsx' },
];

function ExportButtons({ params, stamp, testPrefix }) {
  const [busy, setBusy] = useState('');
  const run = async (format, ext) => {
    setBusy(format);
    try {
      const res = await releaseAPI.export(format, params);
      downloadBlob(res.data, `mayo-release-features-${stamp}.${ext}`);
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setBusy('');
    }
  };
  return (
    <div className="flex items-center gap-1.5">
      {EXPORTS.map(({ format, label, icon: Icon, ext }) => (
        <Button
          key={format}
          variant="outline"
          size="sm"
          onClick={() => run(format, ext)}
          disabled={busy === format}
          data-testid={`${testPrefix}-export-${format}`}
        >
          {busy === format ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Icon size={13} className="mr-1" />}
          {label}
        </Button>
      ))}
    </div>
  );
}

function SummaryRow({ item, isAdmin, onChanged, onOpenTask }) {
  const lines = (item.content || '').split('\n').filter(l => l.trim());

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this summary?')) return;
    try {
      await releaseAPI.delete(item.id);
      toast.success('Summary deleted');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleDeleteShot = async (id) => {
    try {
      await deleteAttachment(id);
      onChanged();
    } catch (err) {
      toast.error('Failed to delete screenshot');
    }
  };

  return (
    <div
      onClick={() => onOpenTask(item.dev_task_id)}
      className="border border-[#E0E0E0] rounded-lg bg-white p-4 cursor-pointer transition-colors hover:border-[#CFFF40]"
      data-testid={`week-summary-${item.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#111111] flex items-center gap-1.5">
            {item.task_title}
            <ExternalLink size={13} className="text-[#999999]" />
          </p>
          <div className="flex items-center gap-2 flex-wrap mt-1.5">
            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#CFFF40] text-[#101010]">
              {item.module_name || 'General'}
            </span>
            {item.restaurant_name && (
              <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#FFF4EC] text-[#B45309] flex items-center gap-1">
                <Store size={10} /> {item.restaurant_name}
              </span>
            )}
            <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#F3F8FF] text-[#6B7280]">
              {item.assignee_name || item.author_name || '—'}
            </span>
            <span className="text-[11px] text-[#999999] flex items-center gap-1">
              <Calendar size={11} />
              {new Date(item.summary_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
            <span className="text-[11px] uppercase tracking-wide text-[#999999]">{item.task_type}</span>
            {item.screenshots?.length > 0 && (
              <span className="text-[11px] text-[#999999] flex items-center gap-1">
                <ImageIcon size={11} /> {item.screenshots.length}
              </span>
            )}
          </div>
        </div>
        {isAdmin && (
          <button onClick={handleDelete} className="text-[#999999] hover:text-[#DC2626] shrink-0" title="Delete summary" data-testid={`week-delete-summary-${item.id}`}>
            <Trash2 size={15} />
          </button>
        )}
      </div>

      <ul className="mt-3 space-y-1">
        {lines.map((line, idx) => (
          <li key={idx} className="text-sm text-[#444444] flex gap-2">
            <span className="text-[#CFFF40] font-bold">•</span>
            <span>{line.replace(/^[-•]\s*/, '')}</span>
          </li>
        ))}
      </ul>

      <div onClick={(e) => e.stopPropagation()}>
        <AttachmentGallery attachments={item.screenshots || []} canDelete={isAdmin} onDelete={handleDeleteShot} />
      </div>

      {item.screenshots?.length === 0 && (
        <p className="text-[11px] text-[#999999] mt-2">Click to open the task and attach a screenshot</p>
      )}
    </div>
  );
}

function WeekBlock({ week, filters, isAdmin, canDeck, onChanged, onOpenTask, refreshToken }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deckBusy, setDeckBusy] = useState(false);
  const filterKey = JSON.stringify(filters);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await releaseAPI.listItems({ week_start: week.week_start, ...JSON.parse(filterKey) });
      setItems(res.data);
    } catch (err) {
      toast.error('Failed to load summaries');
    } finally {
      setLoading(false);
    }
  }, [week.week_start, filterKey]);

  useEffect(() => {
    if (open) fetchItems();
  }, [open, fetchItems, refreshToken]);

  const handleDeck = async () => {
    setDeckBusy(true);
    try {
      const res = await releaseAPI.pptx({
        week_start: week.week_start,
        module_ids: filters.module_ids ? filters.module_ids.split(',') : null,
        restaurant_ids: filters.restaurant_ids ? filters.restaurant_ids.split(',') : null,
        assignee_id: filters.assignee_id || null,
      });
      downloadBlob(res.data, `mayo-release-${week.week_start}.pptx`);
      toast.success('Deck generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Deck generation failed');
    } finally {
      setDeckBusy(false);
    }
  };

  const weekParams = { week_start: week.week_start, ...filters };

  return (
    <div className="bg-white border border-[#E0E0E0] rounded-lg overflow-hidden" data-testid={`release-week-${week.week_start}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3">
        <button
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-3 flex-1 min-w-0 text-left"
          data-testid={`toggle-week-${week.week_start}`}
        >
          {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          <div className="min-w-0">
            <p className="font-semibold text-[#111111] truncate">{week.week_label}</p>
            <p className="text-xs text-[#999999] truncate">
              {week.modules.length ? week.modules.join(' · ') : 'No module'}
              {week.restaurants?.length ? ` — ${week.restaurants.join(', ')}` : ''}
              {week.owners.length ? ` — ${week.owners.join(', ')}` : ''}
            </p>
          </div>
          <Badge variant="secondary" className="ml-auto shrink-0">{week.count}</Badge>
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportButtons params={weekParams} stamp={week.week_start} testPrefix={`week-${week.week_start}`} />
          {canDeck && (
            <Button
              size="sm"
              onClick={handleDeck}
              disabled={deckBusy}
              className="bg-[#101010] hover:bg-[#2A2A2A] text-[#CFFF40]"
              data-testid={`week-pptx-${week.week_start}`}
            >
              {deckBusy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Presentation size={14} className="mr-1" />}
              {deckBusy ? 'Generating...' : 'AI PPTX'}
            </Button>
          )}
        </div>
      </div>

      {open && (
        <div className="bg-[#F5F5F5] border-t border-[#E0E0E0] p-3 space-y-3" data-testid={`week-body-${week.week_start}`}>
          {loading && <p className="text-sm text-[#666666]">Loading...</p>}
          {!loading && items?.length === 0 && <p className="text-sm text-[#666666]">No summaries for these filters.</p>}
          {!loading && items?.map(item => (
            <SummaryRow
              key={item.id}
              item={item}
              isAdmin={isAdmin}
              onChanged={() => { fetchItems(); onChanged(); }}
              onOpenTask={onOpenTask}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ReleaseFeaturesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canDeck = ['admin', 'partner'].includes(user?.role);
  const canSeeOwnerFilter = ['admin', 'partner'].includes(user?.role);

  const [weeks, setWeeks] = useState([]);
  const [modules, setModules] = useState([]);
  const [users, setUsers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [moduleIds, setModuleIds] = useState([]);
  const [restaurantIds, setRestaurantIds] = useState([]);
  const [assigneeId, setAssigneeId] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [modalTask, setModalTask] = useState(undefined);

  const filters = {};
  if (moduleIds.length) filters.module_ids = moduleIds.join(',');
  if (restaurantIds.length) filters.restaurant_ids = restaurantIds.join(',');
  if (assigneeId) filters.assignee_id = assigneeId;
  const filterKey = JSON.stringify(filters);

  const listParams = { ...filters };
  if (dateFrom) listParams.date_from = dateFrom;
  if (dateTo) listParams.date_to = dateTo;

  const fetchWeeks = useCallback(async () => {
    setLoading(true);
    try {
      const params = JSON.parse(filterKey);
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await releaseAPI.listWeeks(params);
      setWeeks(res.data);
    } catch (err) {
      toast.error('Failed to load weeks');
    } finally {
      setLoading(false);
    }
  }, [filterKey, dateFrom, dateTo]);

  useEffect(() => { fetchWeeks(); }, [fetchWeeks, reloadKey]);

  useEffect(() => {
    (async () => {
      try {
        const [modulesRes, restaurantsRes, usersRes] = await Promise.all([
          modulesAPI.getAll(),
          restaurantsAPI.getAll().catch(() => ({ data: [] })),
          canSeeOwnerFilter ? usersAPI.getAll().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        ]);
        setModules(modulesRes.data);
        setRestaurants(restaurantsRes.data);
        setUsers(usersRes.data.filter(u => ['admin', 'developer'].includes(u.role)));
      } catch (err) {
        // filters degrade gracefully
      }
    })();
  }, [canSeeOwnerFilter]);

  const openTask = async (taskId) => {
    try {
      const res = await tasksAPI.getOne(taskId);
      setModalTask(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not open the task');
    }
  };

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setModuleIds([]);
    setRestaurantIds([]);
    setAssigneeId('');
  };

  const hasFilters = dateFrom || dateTo || moduleIds.length || restaurantIds.length || assigneeId;
  const totalSummaries = weeks.reduce((sum, w) => sum + w.count, 0);

  return (
    <DashboardLayout pageTitle="Release Features">
      <div className="space-y-4" data-testid="release-features-page">
        <div className="flex flex-col xl:flex-row xl:items-end gap-3 bg-white border border-[#E0E0E0] rounded-lg p-3">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 flex-1">
            <div>
              <label className="text-[11px] uppercase tracking-wide text-[#999999]">From</label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9" data-testid="release-filter-from" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-[#999999]">To</label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9" data-testid="release-filter-to" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-[#999999]">Modules</label>
              <MultiSelect
                options={modules.map(m => ({ id: m.id, name: m.name }))}
                selected={moduleIds}
                onChange={setModuleIds}
                placeholder="All modules"
                testId="release-filter-module"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-[#999999]">Restaurants</label>
              <MultiSelect
                options={restaurants.map(r => ({ id: r.id, name: r.name }))}
                selected={restaurantIds}
                onChange={setRestaurantIds}
                placeholder="All restaurants"
                testId="release-filter-restaurant"
              />
            </div>
            {canSeeOwnerFilter && (
              <div>
                <label className="text-[11px] uppercase tracking-wide text-[#999999]">Owner</label>
                <select
                  value={assigneeId}
                  onChange={(e) => setAssigneeId(e.target.value)}
                  className="w-full h-9 px-2 border border-[#E0E0E0] rounded-md text-sm bg-white"
                  data-testid="release-filter-owner"
                >
                  <option value="">All owners</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {hasFilters ? (
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="release-clear-filters">Clear</Button>
            ) : null}
            <ExportButtons params={listParams} stamp="all" testPrefix="main" />
          </div>
        </div>

        <p className="text-sm text-[#666666]" data-testid="release-total">
          {weeks.length} week(s) · {totalSummaries} summaries
        </p>

        {loading && (
          <div className="flex items-center justify-center h-40">
            <div className="w-8 h-8 border-4 border-[#CFFF40] border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {!loading && weeks.length === 0 && (
          <div className="bg-white border border-[#E0E0E0] rounded-lg p-10 text-center text-[#666666]" data-testid="release-empty-state">
            No release summaries yet. Add them from a task card in Tasks.
          </div>
        )}

        {!loading && weeks.map(week => (
          <WeekBlock
            key={week.week_start}
            week={week}
            filters={filters}
            isAdmin={isAdmin}
            canDeck={canDeck}
            refreshToken={reloadKey}
            onChanged={() => setReloadKey(k => k + 1)}
            onOpenTask={openTask}
          />
        ))}
      </div>

      {modalTask !== undefined && (
        <DevTaskModal
          task={modalTask}
          modules={modules}
          users={users}
          restaurants={restaurants}
          isAdmin={isAdmin}
          currentUser={user}
          onClose={() => { setModalTask(undefined); setReloadKey(k => k + 1); }}
          onSaved={() => { setModalTask(undefined); setReloadKey(k => k + 1); }}
        />
      )}
    </DashboardLayout>
  );
}
